/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { Box, Container, Link, Typography, styled } from '@mui/material'
import BackgroundImage from 'assets/images/watermark.png'
import { MButton, Text } from 'components'
import { ROUTES_PATH } from 'config'
import { color } from 'config/color'
import type React from 'react'
import { useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMobileBreakpoints } from 'utils/hooks/useMobileBreakpoints'

interface IAuth {
  title?: string
  desc?: string
  subTitle?: string
  children: React.ReactNode
  isAuthPage?: boolean
}

const { REACT_APP_CLIENT_LOGO } = process.env

export const AuthLayout = ({
  title,
  desc,
  isAuthPage = false,
  children
}: IAuth): JSX.Element => {
  const navigate = useNavigate()
  const location = useLocation()
  const isMobile = useMobileBreakpoints()

  const isFullScreen = useMemo(() => {
    if (location.pathname === ROUTES_PATH.FORGOT_PASSWORD || location.pathname === ROUTES_PATH.SIGNIN || location.pathname === ROUTES_PATH.NEW_PASSWORD) {
      return true
    }

    return false
  }, [location.pathname])

  return (
    <>
      <Wrapper isFullScreen={isFullScreen} isMobile={isMobile}>
        {isAuthPage
          ? (
            <Box display="flex" flexDirection="column" mt={5} mb={2} gap={3} alignItems="center" justifyContent="center">
              <Link href="/" my={3}><img src={REACT_APP_CLIENT_LOGO} height={50} alt="Logo" /></Link>
              <Typography textAlign="center" color={color.grey[900]} fontSize={32}>{title}</Typography>
              <Typography textAlign="center" color={color.grey[700]} fontSize={18} width={isMobile ? '90%' : '60%'}>{desc}</Typography>
            </Box>
          )
          : (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              py={4}
              px={3}
              sx={{ backgroundColor: isMobile ? color.white : color.secondary.dark[50], borderBottom: isMobile ? `solid 1px ${color.white}` : '0px' }}
            >
              <Box display="flex" alignItems="center">
                <Link href="/">
                  <img src={REACT_APP_CLIENT_LOGO} height={isMobile ? 25 : 40} alt="Logo" />
                </Link>
              </Box>
              <MButton
                variant="outlined"
                rounded="true"
                texttransform="none"
                onClick={() => {
                  navigate(ROUTES_PATH.SIGNIN)
                }}
              >
                Back to Login
              </MButton>
            </Box>
          )}
        <Container>
          <Content>
            <Box width="100%" mb={5}>
              {children}
            </Box>
          </Content>
          <Background src={BackgroundImage} alt="Background" />
          <Footer>
            <Text color={color.grey[300]} fontSize="14px">
              © {new Date().getFullYear()}. All rights reserved.
            </Text>
          </Footer>
        </Container>
      </Wrapper >
    </>
  )
}

const Wrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isFullScreen' && prop !== 'isMobile'
})<{ isFullScreen?: boolean, isMobile?: boolean }>(
  ({ isFullScreen, isMobile }) => ({
    background:
      isFullScreen && !isMobile
        ? `linear-gradient(to bottom, ${color.secondary.dark[50]} 50%, ${color.secondary.dark[900]} 50%)`
        : `linear-gradient(to bottom, ${color.secondary.dark[50]} 50vh, ${color.secondary.dark[900]} 50vh)`,


    display: 'flex',
    flexDirection: 'column',
    height: isFullScreen && !isMobile ? '100vh' : '100%'
  })
)

const Content = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'space-between',
  position: 'relative',
  zIndex: 1
})

const Footer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  position: 'relative',
  paddingTop: '3vh',
  paddingBottom: '3vh'
})

const Background = styled('img')({
  backgroundRepeat: 'no-repeat',
  position: 'fixed',
  zIndex: 0,
  left: 0,
  bottom: 0,
  width: '100%',
  height: '50%'
})
